import { create } from 'zustand';
import { apiPost, setAccessToken, registerSessionExpiredHandler } from '../lib/apiClient';
import { login as loginRequest, logout as logoutRequest, fetchCurrentUser } from '../services/authApi';
import { fetchBusinessSettings } from '../services/businessApi';
import { connectSocket, disconnectSocket } from '../lib/socket';
import type { AuthUser, BusinessSettings, RefreshResponse } from '../types/auth';

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  business: BusinessSettings | null;
  signingIn: boolean;

  bootstrap: () => Promise<void>;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshBusiness: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

async function loadBusiness(set: (partial: Partial<AuthState>) => void) {
  try {
    const business = await fetchBusinessSettings();
    set({ business });
  } catch {
    // Non-fatal — the shell can render without it.
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'initializing',
  user: null,
  business: null,
  signingIn: false,

  bootstrap: async () => {
    try {
      const { accessToken } = await apiPost<RefreshResponse>('/auth/refresh', {});
      setAccessToken(accessToken);
      const { user } = await fetchCurrentUser();
      set({ status: 'authenticated', user });
      connectSocket();
      void loadBusiness(set);
    } catch {
      setAccessToken(null);
      set({ status: 'unauthenticated', user: null });
    }
  },

  signIn: async (identifier, password) => {
    set({ signingIn: true });
    try {
      const result = await loginRequest(identifier, password);
      setAccessToken(result.accessToken);
      set({ status: 'authenticated', user: result.user });
      connectSocket();
      void loadBusiness(set);
    } finally {
      set({ signingIn: false });
    }
  },

  refreshBusiness: async () => {
    await loadBusiness(set);
  },

  signOut: async () => {
    try {
      await logoutRequest();
    } catch {
      // Ignore — clear local state regardless.
    }
    disconnectSocket();
    setAccessToken(null);
    set({ status: 'unauthenticated', user: null, business: null });
  },

  hasPermission: (permission) => {
    const perms = get().user?.role.permissions ?? [];
    return perms.includes('*') || perms.includes(permission);
  },

  hasAnyPermission: (permissions) => {
    const perms = get().user?.role.permissions ?? [];
    if (perms.includes('*')) return true;
    return permissions.some((p) => perms.includes(p));
  },
}));

// When the api client gives up on refreshing, drop to the login screen.
registerSessionExpiredHandler(() => {
  disconnectSocket();
  setAccessToken(null);
  useAuthStore.setState({ status: 'unauthenticated', user: null, business: null });
});
